import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './dialog';
import { Button, type ButtonProps } from './button';
import { cn } from '@/lib/utils';

interface AlertDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: React.ReactNode;
}

function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {children}
        </Dialog>
    );
}

function AlertDialogContent({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <DialogContent {...props} className={cn('max-h-[90vh] flex flex-col', props.className)}>
            {children}
        </DialogContent>
    );
}

function AlertDialogHeader({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <DialogHeader {...props} className={cn('flex-shrink-0', props.className)}>
            {children}
        </DialogHeader>
    );
}

function AlertDialogTitle({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
    return <DialogTitle {...props}>{children}</DialogTitle>;
}

function AlertDialogDescription({
    children,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
    return <DialogDescription {...props}>{children}</DialogDescription>;
}

function AlertDialogBody({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div {...props} className={cn('flex-1 overflow-y-auto min-h-0', props.className)}>
            {children}
        </div>
    );
}

function AlertDialogFooter({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <DialogFooter {...props} className={cn('flex-shrink-0', props.className)}>
            {children}
        </DialogFooter>
    );
}

function AlertDialogCancel({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <Button variant="outline" {...props}>
            {children}
        </Button>
    );
}

function AlertDialogAction({ children, ...props }: ButtonProps) {
    return <Button {...props}>{children}</Button>;
}

export {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
};
